import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Col, Empty, Row, Spin, Statistic, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DownloadOutlined,
  MinusCircleOutlined,
  CarOutlined,
  FieldTimeOutlined,
  InboxOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Pie, Column } from '@ant-design/charts';
import * as partnerApi from '../modules/partners/api';
import StatsFilters, { formatStatsRange } from '../components/StatsFilters';
import StatsByName from '../components/StatsByName';
import ProfitabilitySimulator from '../components/ProfitabilitySimulator';
import { downloadStatsCsv } from '../utils/statsCsvExport';
import './PartnerStats.scss';

// PERIOD_OPTIONS moved inside component to use translations

const STATUS_COLORS = {
  COMPLETED: '#2e7d32',
  DELIVERY_FAILED: '#c62828',
  SKIPPED: '#f57c00',
  ASSIGNED: '#1565c0',
  UNASSIGNED: '#9e9e9e',
  DISPATCHED: '#4527a0',
  COLLECTED: '#6a1b9a',
  PICKUP_ARRIVED: '#6a1b9a',
  DELIVERY_ARRIVED: '#6a1b9a',
  SERVICE_ARRIVED: '#6a1b9a',
  OPTIMIZED: '#00838f',
  ERROR: '#b71c1c',
  DRAFT: '#bdbdbd',
  CANCELLED: '#9c27b0',
};

const CATEGORY_COLORS = {
  PRODUCT: '#1565c0',
  EMPTY: '#f57c00',
  UNKNOWN: '#9e9e9e',
};

const formatMins = (mins) => {
  if (!mins || mins <= 0) return '0m';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const PartnerStats = ({ partnerId, viewOnly, partnerName }) => {
  const { t } = useTranslation('stats');

  const [filter, setFilter] = useState({ period: 'this_week', from: undefined, to: undefined });
  // Per-client visit stats now use the unified `byClient` block from the
  // backend (normalized name + per-day dedup + pickup/delivery split).
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    const query = { period: filter.period, from: filter.from, to: filter.to };
    const { response } = viewOnly
      ? await partnerApi.getMyPartnerStats(query)
      : await partnerApi.getPartnerStats(partnerId, query);
    if (response) setStats(response);
    setLoading(false);
  }, [partnerId, filter.period, filter.from, filter.to, viewOnly]);

  useEffect(() => {
    if (partnerId || viewOnly) fetchStats();
  }, [fetchStats, partnerId, viewOnly]);

  const handleExport = () => {
    if (!stats) return;
    downloadStatsCsv(stats, {
      title: viewOnly ? 'Partner stats (viewer)' : `Partner ${partnerId} stats`,
      partnerName,
      filenameSlug: partnerName
        ? `stats-${partnerName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`
        : `stats-partner-${partnerId || 'me'}`,
    });
  };

  const filtersAndExport = (
    <StatsFilters
      value={filter}
      onChange={setFilter}
      resolvedRange={stats?.period}
      rightSlot={
        <Button icon={<DownloadOutlined />} onClick={handleExport} disabled={!stats || loading}>
          Export CSV
        </Button>
      }
    />
  );

  if (loading && !stats) {
    return (
      <div className="partner-stats">
        <Spin style={{ display: 'block', paddingTop: 80 }} />
      </div>
    );
  }

  if (!stats || stats.delivery.total === 0) {
    return (
      <div className="partner-stats">
        {filtersAndExport}
        {loading ? (
          <Spin style={{ display: 'block', paddingTop: 80 }} />
        ) : (
          <Empty description={t('noDataForPeriod')} style={{ paddingTop: 60 }} />
        )}
      </div>
    );
  }

  // Chart data
  const statusData = Object.entries(stats.time.ordersByStatus).map(([status, value]) => ({
    type: status.replace(/_/g, ' '),
    value,
    color: STATUS_COLORS[status] || '#9e9e9e',
  }));

  const onTimeData = [
    { type: 'On time', value: stats.time.onTimeCount, color: '#2e7d32' },
    { type: 'Late', value: stats.time.lateCount, color: '#c62828' },
  ].filter((d) => d.value > 0);

  const categoryData = Object.entries(stats.collection.byCategory).map(([cat, value]) => ({
    type: cat,
    value,
    color: CATEGORY_COLORS[cat] || '#9e9e9e',
  }));

  const dowOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const dowData = dowOrder.map((day) => ({
    day,
    orders: stats.time.ordersByDayOfWeek[day] || 0,
  }));

  const pieConfig = {
    height: 240,
    innerRadius: 0.6,
    radius: 0.85,
    label: { text: 'value', style: { fontWeight: 600 } },
    legend: { color: { position: 'bottom', layout: { justifyContent: 'center' } } },
    tooltip: { title: 'type' },
    style: { lineWidth: 2, stroke: '#fff' },
  };

  // Mirror of the prominent range chip from StatsFilters — small, inline,
  // appended to every section title so the time context is visible no matter
  // how far the operator scrolls.
  const rangeText = formatStatsRange(stats?.period);
  const sectionTitle = (label) => (
    <div className="section-title">
      <span>{label}</span>
      <span className="section-range">{rangeText}</span>
    </div>
  );
  // Card-title variant for chart/byClient cards — same styling as sectionTitle
  // but compact and flex-row so it slots into Ant Design's card head. Pass a
  // helpKey to append an explanation tooltip (admin only, see `help`).
  const cardTitle = (label, helpKey) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
      <span>
        {label}
        {helpKey && !viewOnly && (
          <Tooltip title={t(`help.${helpKey}`)} overlayStyle={{ maxWidth: 380 }}>
            <InfoCircleOutlined style={{ marginLeft: 6, color: '#9e9e9e' }} />
          </Tooltip>
        )}
      </span>
      <span className="section-range">{rangeText}</span>
    </div>
  );

  // OVO-268 follow-up (Diego, Aug 2026) — every KPI gets an inline explanation:
  // the figures are correct but easy to MISREAD (distance/CO₂ describe the
  // whole routes serving the partner, on-time is plan-based, prise de service
  // is cost not billable time). ADMIN-ONLY: several texts explain shared-route
  // mechanics, and a partner viewer must never learn a route is shared
  // (OVO-002) — viewers keep the plain titles.
  const help = (label, helpKey) =>
    viewOnly ? (
      label
    ) : (
      <span>
        {label}
        <Tooltip title={t(`help.${helpKey}`)} overlayStyle={{ maxWidth: 380 }}>
          <InfoCircleOutlined style={{ marginLeft: 6, color: '#9e9e9e' }} />
        </Tooltip>
      </span>
    );

  return (
    <div className="partner-stats">
      {filtersAndExport}

      <Spin spinning={loading}>
        {/* Delivery Performance */}
        <div className="stats-section">
          {sectionTitle('Delivery Performance')}
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card">
                <Statistic
                  title={help('Total', 'total')}
                  value={stats.delivery.total}
                  valueStyle={{ color: '#212121' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card success">
                <Statistic
                  title={help('Completed', 'completed')}
                  value={stats.delivery.completed}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#2e7d32' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card danger">
                <Statistic
                  title={help('Failed', 'failed')}
                  value={stats.delivery.failed}
                  prefix={<CloseCircleOutlined />}
                  valueStyle={{ color: '#c62828' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card warning">
                <Statistic
                  title={help('Skipped', 'skipped')}
                  value={stats.delivery.skipped}
                  prefix={<MinusCircleOutlined />}
                  valueStyle={{ color: '#f57c00' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8} md={4}>
              <Card className="stat-card">
                <Statistic
                  title={help('Completion Rate', 'completionRate')}
                  value={stats.delivery.completionRate}
                  suffix="%"
                  precision={1}
                />
              </Card>
            </Col>
          </Row>
        </div>

        {/* Charts Row 1: Status + On-Time */}
        <Row gutter={[16, 16]} className="stats-section">
          <Col xs={24} md={12}>
            <Card className="chart-card" title={cardTitle('Orders by Status')}>
              {statusData.length > 0 ? (
                <Pie
                  data={statusData}
                  angleField="value"
                  colorField="type"
                  color={statusData.map((d) => d.color)}
                  {...pieConfig}
                />
              ) : (
                <Empty description="No data" />
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="chart-card" title={cardTitle('On-Time Performance', 'onTime')}>
              {onTimeData.length > 0 ? (
                <Pie
                  data={onTimeData}
                  angleField="value"
                  colorField="type"
                  color={['#2e7d32', '#c62828']}
                  {...pieConfig}
                />
              ) : (
                <Empty description="No delivery time data" />
              )}
            </Card>
          </Col>
        </Row>

        {/* Route Operations */}
        <div className="stats-section">
          {sectionTitle('Route Operations')}
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic title={help('Routes', 'routes')} value={stats.routes.total} prefix={<CarOutlined />} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title={help('Avg Stops / Route', 'avgStops')}
                  value={stats.routes.avgStopsPerRoute}
                  precision={1}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title={help('Total Distance', 'totalDistance')}
                  value={stats.routes.totalDistanceKm}
                  suffix="km"
                  precision={1}
                />
              </Card>
            </Col>
            {/* OVO-130 — client service time (first pickup → last delivery),
                total + average. The shift window (payroll) is intentionally
                not shown here — partners are billed on service time. */}
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title={help('Total Service Time', 'serviceTime')}
                  value={formatMins(stats.routes.totalClientServiceMins ?? 0)}
                  prefix={<FieldTimeOutlined />}
                  formatter={(val) => val}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title={help('Avg Service / Route', 'avgService')}
                  value={formatMins(stats.routes.avgClientServiceMins ?? stats.routes.avgDurationMins)}
                  prefix={<FieldTimeOutlined />}
                  formatter={(val) => val}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title={help('Total Prise de service', 'prise')}
                  value={formatMins(stats.routes.totalPriseDeServiceMins ?? 0)}
                  prefix={<FieldTimeOutlined />}
                  formatter={(val) => val}
                />
              </Card>
            </Col>
            {/* OVO-106 — CO2 total. Greyed out if no route in the period
                belongs to a fleet with a configured factor. */}
            {stats.routes.totalCo2Kg != null && (
              <>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title={help('Total CO₂', 'co2')}
                      value={stats.routes.totalCo2Kg}
                      suffix="kg"
                      precision={2}
                      valueStyle={{ color: '#2e7d32' }}
                    />
                  </Card>
                </Col>
                <Col xs={12} sm={6}>
                  <Card className="stat-card">
                    <Statistic
                      title={help('Avg CO₂ / Route', 'co2Avg')}
                      value={stats.routes.avgCo2KgPerRoute}
                      suffix="kg"
                      precision={2}
                    />
                  </Card>
                </Col>
                {stats.routes.totalCo2SavedKg > 0 && (
                  <Col xs={12} sm={6}>
                    <Tooltip
                      title={`Compared to a reference vehicle at ${stats.routes.oilBaselineGPerKm} g CO₂-eq/km (default: diesel car, Mobitool v3.1)`}
                    >
                      <Card className="stat-card stat-card-co2-saved">
                        <Statistic
                          title={
                            <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                              <span role="img" aria-label="eco" style={{ marginRight: 6 }}>
                                🌱
                              </span>
                              CO₂ saved vs car
                            </span>
                          }
                          value={stats.routes.totalCo2SavedKg}
                          suffix="kg"
                          precision={2}
                          prefix={
                            <span role="img" aria-label="leaf" style={{ color: '#2e7d32', marginRight: 4 }}>
                              🌿
                            </span>
                          }
                          valueStyle={{ color: '#2e7d32', fontWeight: 700 }}
                        />
                      </Card>
                    </Tooltip>
                  </Col>
                )}
              </>
            )}
          </Row>
        </div>

        {/* OVO-268 follow-up — client-side profitability simulator (Diego, Aug
            2026). ADMIN-ONLY: driver cost and margins must never reach a
            partner viewer (OVO-002). */}
        {!viewOnly && <ProfitabilitySimulator stats={stats} />}

        {/* Charts Row 2: Items + Day of Week */}
        <Row gutter={[16, 16]} className="stats-section">
          <Col xs={24} md={12}>
            <Card className="chart-card" title={cardTitle('Items by Category')}>
              {categoryData.length > 0 ? (
                <Pie
                  data={categoryData}
                  angleField="value"
                  colorField="type"
                  color={categoryData.map((d) => d.color)}
                  {...pieConfig}
                />
              ) : (
                <Empty description="No items collected" />
              )}
            </Card>
          </Col>
          <Col xs={24} md={12}>
            <Card className="chart-card" title={cardTitle('Orders by Day of Week')}>
              <Column
                data={dowData}
                xField="day"
                yField="orders"
                height={240}
                color="#212121"
                label={{ position: 'top', style: { fontWeight: 600 } }}
                xAxis={{ label: { style: { fontSize: 12 } } }}
              />
            </Card>
          </Col>
        </Row>

        {/* Collection & Time */}
        <div className="stats-section">
          {sectionTitle('Collection & Time')}
          <Row gutter={[16, 16]}>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic title="Items Collected" value={stats.collection.totalItems} prefix={<InboxOutlined />} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic title="Items / Route" value={stats.collection.avgItemsPerRoute} precision={1} />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title={
                    <Tooltip title="Average time per task — from the previous stop's completion to this stop's completion (approach travel + time on site). Uses actual badged times, falling back to the planned ETA when a stop wasn't logged.">
                      Avg time / task
                    </Tooltip>
                  }
                  value={formatMins(stats.time.avgTimePerStopMins)}
                  formatter={(val) => val}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card className="stat-card">
                <Statistic
                  title="Return Drop-off"
                  value={stats.collection.returnDropoffRate}
                  suffix="%"
                  precision={1}
                />
              </Card>
            </Col>
          </Row>
        </div>

        {/* OVO-111 — visits per client */}
        <div className="stats-section">
          <StatsByName data={stats.byClient} loading={loading} titleSuffix={rangeText} />
        </div>

        {/* OVO-106 — methodology + reference link. Shown to partner viewers
            and admins alike so the source of the CO2 numbers is obvious. */}
        {stats.routes.totalCo2Kg != null && (
          <div className="stats-section" style={{ fontSize: 12, color: '#8c8c8c', paddingTop: 8 }}>
            CO₂ estimates use per-vehicle-km emission factors from{' '}
            <a
              href="https://www.suisseenergie.ch/programmes/calculateur-environnemental-transport/"
              target="_blank"
              rel="noopener noreferrer"
            >
              SuisseEnergie's transport environmental calculator
            </a>{' '}
            (Mobitool v3.1). Savings are computed against a configurable reference vehicle (
            {stats.routes.oilBaselineGPerKm} g CO₂-eq/km — default: average diesel passenger car).
          </div>
        )}
      </Spin>
    </div>
  );
};

export default PartnerStats;
